"use client";

import { motion } from "framer-motion";
import { CheckCircle2, LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

export interface Step {
  id: string;
  icon: LucideIcon;
  number: string;
}

const StepVisual = ({
  id,
  icon: Icon,
  isFirstView,
}: {
  id: string;
  icon: LucideIcon;
  isFirstView: boolean;
}) => {
  switch (id) {
    case "discovery":
      return (
        <div className="relative w-full h-125 flex items-center justify-center">
          {/* Orbital rings */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{
                rotate: 360,
                scale: [1, 1.05, 1],
              }}
              transition={{
                rotate: {
                  duration: 20 + i * 5,
                  repeat: Infinity,
                  ease: "linear",
                },
                scale: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i,
                },
              }}
              className={cn(
                "absolute rounded-full border border-(--brand-primary)/20 border-dashed",
                i === 0 ? "w-70 h-70" : i === 1 ? "w-95 h-95" : "w-120 h-120",
              )}
            />
          ))}
          {/* Core Idea */}
          <div className="relative z-10 w-24 h-24 rounded-full bg-(--bg-card) border border-(--border-subtle) shadow-[0_0_50px_var(--brand-glow)] flex items-center justify-center">
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon className="w-12 h-12 text-(--brand-primary)" />
            </motion.div>
          </div>
        </div>
      );
    case "strategy":
      return (
        <div className="relative w-full h-125 flex items-center justify-center">
          {/* Node Network Map */}
          <div className="relative w-100 h-100">
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
            >
              {[
                { d: "M20,50 L50,20", delay: 0 },
                { d: "M50,20 L80,50", delay: 0.5 },
                { d: "M80,50 L50,80", delay: 1 },
                { d: "M50,80 L20,50", delay: 1.5 },
                { d: "M20,50 L80,50", delay: 2 },
              ].map((path, i) => (
                <motion.path
                  key={i}
                  d={path.d}
                  fill="transparent"
                  stroke="var(--border-subtle)"
                  strokeWidth="0.5"
                  initial={
                    isFirstView
                      ? { pathLength: 0, opacity: 0 }
                      : { pathLength: 1, opacity: 1 }
                  }
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: 1.5,
                    delay: isFirstView ? path.delay : 0,
                    ease: "easeOut",
                  }}
                />
              ))}
              {[
                { d: "M20,50 L50,20", delay: 0 },
                { d: "M50,20 L80,50", delay: 0.5 },
                { d: "M80,50 L50,80", delay: 1 },
              ].map((path, i) => (
                <motion.path
                  key={`glow-${i}`}
                  d={path.d}
                  fill="transparent"
                  stroke="var(--brand-primary)"
                  strokeWidth="1"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: [0, 1, 0], opacity: [0, 1, 0] }}
                  transition={{
                    duration: 3,
                    delay: path.delay,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </svg>
            {[
              { x: "20%", y: "50%" },
              { x: "50%", y: "20%" },
              { x: "80%", y: "50%" },
              { x: "50%", y: "80%" },
              { x: "50%", y: "50%" },
            ].map((pos, i) => (
              <div
                key={i}
                className="absolute w-4 h-4 rounded-full bg-(--bg-card) border-2 border-(--brand-primary) shadow-[0_0_15px_var(--brand-glow)] -ml-2 -mt-2"
                style={{ left: pos.x, top: pos.y }}
              >
                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                  className="w-full h-full bg-(--brand-primary) rounded-full"
                />
              </div>
            ))}
          </div>
        </div>
      );
    case "design":
      return (
        <div className="relative w-full h-125 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--brand-primary)_0%,transparent_60%)] opacity-[0.03]" />

          <div className="relative w-87.5 h-112.5 bg-(--bg-card) border border-(--border-subtle) rounded-2xl shadow-2xl p-6 flex flex-col gap-4 overflow-hidden">
            {/* Mock UI Frame */}
            <div className="flex gap-2 border-b border-(--border-subtle) pb-4">
              <div className="w-3 h-3 rounded-full bg-(--border-subtle)" />
              <div className="w-3 h-3 rounded-full bg-(--border-subtle)" />
            </div>
            <motion.div
              initial={isFirstView ? { width: "0%" } : { width: "60%" }}
              animate={{ width: "60%" }}
              transition={{ duration: 1 }}
              className="h-8 bg-(--border-subtle)/50 rounded-lg"
            />
            <motion.div
              initial={isFirstView ? { width: "0%" } : { width: "100%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 1, delay: isFirstView ? 0.2 : 0 }}
              className="h-32 bg-(--brand-primary)/10 border border-(--brand-primary)/20 rounded-lg relative overflow-hidden"
            >
              <motion.div
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-y-0 w-1/2 bg-linear-to-r from-transparent via-(--brand-primary)/20 to-transparent skew-x-12"
              />
            </motion.div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <motion.div
                initial={isFirstView ? { opacity: 0 } : { opacity: 1 }}
                animate={{ opacity: 1 }}
                transition={{ delay: isFirstView ? 0.4 : 0 }}
                className="h-24 bg-(--border-subtle)/30 rounded-lg"
              />
              <motion.div
                initial={isFirstView ? { opacity: 0 } : { opacity: 1 }}
                animate={{ opacity: 1 }}
                transition={{ delay: isFirstView ? 0.6 : 0 }}
                className="h-24 bg-(--border-subtle)/30 rounded-lg"
              />
            </div>
          </div>

          <motion.div
            animate={{ y: [-10, 10, -10] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-(--bg-primary) rounded-full border border-(--border-subtle) shadow-xl flex items-center justify-center"
          >
            <Icon className="w-8 h-8 text-(--brand-primary)" />
          </motion.div>
        </div>
      );
    case "development":
      return (
        <div className="relative w-full h-125 flex items-center justify-center">
          <div className="w-full max-w-112.5 rounded-xl bg-(--terminal-bg) border border-(--terminal-border) shadow-2xl overflow-hidden font-mono text-[13px]">
            <div className="bg-(--terminal-header) px-4 py-3 flex items-center gap-2 border-b border-(--terminal-border)">
              <div className="w-3 h-3 rounded-full bg-(--brand-primary)" />
              <div className="w-3 h-3 rounded-full bg-(--brand-yellow)" />
              <div className="w-3 h-3 rounded-full bg-(--brand-green)" />
              <span className="ml-4 text-[11px] text-(--terminal-text-muted)">
                Terminal - zsh
              </span>
            </div>
            <div className="p-6 text-(--terminal-text) bg-(--terminal-bg) h-75 flex flex-col gap-2 relative">
              <motion.div
                initial={isFirstView ? { opacity: 0 } : { opacity: 1 }}
                animate={{ opacity: 1 }}
                transition={{ delay: isFirstView ? 0.2 : 0 }}
              >
                <span className="text-(--brand-green)">~</span>$ pnpm run build
              </motion.div>
              {[
                { t: "> Building modular architecture...", d: 0.8 },
                { t: "> Compiling React components...", d: 1.4 },
                { t: "> Optimizing production assets...", d: 2.0 },
                {
                  t: "✓ Compiled successfully in 2.4s",
                  d: 2.8,
                  color: "text-(--brand-primary)",
                },
              ].map((line, i) => (
                <motion.div
                  key={i}
                  initial={isFirstView ? { opacity: 0 } : { opacity: 1 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: isFirstView ? line.d : 0 }}
                  className={line.color}
                >
                  {line.t}
                </motion.div>
              ))}
              <motion.div
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-2 h-4 bg-(--terminal-text-muted) mt-2"
              />
            </div>
          </div>
        </div>
      );
    case "launch":
      return (
        <div className="relative w-full h-125 flex items-center justify-center overflow-hidden">
          {/* Grid floor */}
          <div
            className="absolute inset-x-0 bottom-0 h-50 bg-[linear-gradient(to_top,var(--border-subtle)_1px,transparent_1px),linear-gradient(to_right,var(--border-subtle)_1px,transparent_1px)] bg-size-[40px_40px] mask-[linear-gradient(to_bottom,transparent,black)] opacity-20"
            style={{ transform: "perspective(500px) rotateX(60deg)" }}
          />

          <motion.div
            initial={isFirstView ? { y: 50, scale: 0.8 } : { y: -20, scale: 1 }}
            animate={{ y: -20, scale: 1 }}
            transition={{ type: "spring", bounce: 0.4, duration: 2 }}
            className="relative z-10 w-70 h-87.5 bg-linear-to-b from-(--bg-card)/50 to-(--bg-primary) border border-(--border-subtle) rounded-4xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] flex flex-col items-center justify-center p-8"
          >
            <div className="w-20 h-20 rounded-full bg-(--bg-card) flex items-center justify-center mb-8 border border-(--border-subtle) relative">
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 rounded-full border self border-(--brand-primary)"
              />
              <Icon className="w-10 h-10 text-(--brand-primary)" />
            </div>

            <div className="w-full h-2 bg-(--border-subtle) rounded-full overflow-hidden mb-4">
              <motion.div
                initial={isFirstView ? { width: "0%" } : { width: "100%" }}
                animate={{ width: "100%" }}
                transition={{
                  duration: 2,
                  delay: isFirstView ? 0.5 : 0,
                  ease: "easeInOut",
                }}
                className="h-full bg-(--brand-primary)"
              />
            </div>
            <span className="text-xs font-semibold text-(--text-secondary) uppercase tracking-widest">
              System Online
            </span>
          </motion.div>
        </div>
      );
    default:
      return null;
  }
};

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "5%" : "-5%",
    opacity: 0,
    filter: "blur(8px)",
  }),
  center: {
    x: "0%",
    opacity: 1,
    filter: "blur(0px)",
  },
  exit: (direction: number) => ({
    x: direction < 0 ? "5%" : "-5%",
    opacity: 0,
    filter: "blur(8px)",
  }),
};

export function ProcessSlide({
  step,
  direction,
  isFirstView,
}: {
  step: Step;
  direction: number;
  isFirstView: boolean;
}) {
  const t = useTranslations("Process");
  const tSteps = useTranslations("Process.steps");
  const detailsRaw = tSteps.raw(`${step.id}.details`);
  const details = Array.isArray(detailsRaw) ? detailsRaw : [];

  return (
    <motion.div
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        opacity: { duration: 0.4 },
        filter: { duration: 0.4 },
      }}
      className="absolute inset-0 w-full h-full items-center justify-center p-4 sm:p-12 z-10 flex"
    >
      <div className="w-full max-w-300 mx-auto relative">
        {/* Decorative Step Number - Protruding (Moved Outside overflow-hidden) */}
        <div className="absolute -right-12 md:-right-20 top-0 hidden xl:flex flex-col items-center justify-center h-full z-0 pointer-events-none mix-blend-overlay opacity-40 select-none">
          <span className="text-[7.5rem] font-black font-outfit text-(--text-tertiary) [writing-mode:vertical-rl] leading-none tracking-widest uppercase">
            {step.number}
          </span>
        </div>

        <div className="w-full bg-(--bg-card) border border-(--border-subtle) shadow-2xl rounded-4xl p-8 md:p-12 lg:p-16 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative overflow-hidden">
          {/* Left Column */}
          <div className="flex flex-col justify-center relative z-10">
            {/* Red accent line + Phase label */}
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-0.75 bg-(--brand-primary) rounded-full inline-block shrink-0" />
              <span className="text-sm font-bold tracking-[0.2em] text-(--brand-primary) uppercase">
                {t("phase")} {step.number}
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black text-(--text-primary) leading-[1.1] mb-6">
              {tSteps(`${step.id}.title`)}
            </h3>
            <p className="text-lg md:text-xl text-(--text-secondary) leading-relaxed mb-10">
              {tSteps(`${step.id}.description`)}
            </p>

            {details.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {details.map((detail, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <div className="inline-flex w-6 h-6 rounded-full bg-(--brand-primary)/10 text-(--brand-primary) items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-(--text-secondary) mt-0.75 leading-snug">
                      {detail}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Visual Graphic */}
          <div className="hidden lg:flex items-center justify-center h-full min-h-100 relative bg-(--bg-primary)/30 rounded-3xl border border-(--border-subtle) overflow-hidden p-8 shadow-inner z-10">
            <StepVisual
              id={step.id}
              icon={step.icon}
              isFirstView={isFirstView}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
